import { useEffect } from "react";

export const SITE_URL = "https://nuaprivatewealth.com.au";

function upsertLink(rel) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

function upsertMeta(attr, value) {
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

// Client-side head manager: title, description, canonical, Open Graph URL/title/
// description and one or more JSON-LD blocks. Everything is restored on unmount
// so navigating back to the homepage keeps the values baked into index.html.
export function useSeo({ title, description, path = "", jsonLd = [] }) {
  const jsonLdKey = JSON.stringify(jsonLd);
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const restore = [];

    if (title) {
      const prev = document.title;
      document.title = title;
      restore.push(() => {
        document.title = prev;
      });
    }

    const applyMeta = (attr, value, content) => {
      const { el, created } = upsertMeta(attr, value);
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      restore.push(() => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
    };

    if (description) {
      applyMeta("name", "description", description);
      applyMeta("property", "og:description", description);
    }
    if (title) applyMeta("property", "og:title", title);
    applyMeta("property", "og:url", url);

    const { el: canonical, created: canonicalCreated } = upsertLink("canonical");
    const prevHref = canonical.getAttribute("href");
    canonical.setAttribute("href", url);
    restore.push(() => {
      if (canonicalCreated) canonical.remove();
      else if (prevHref) canonical.setAttribute("href", prevHref);
    });

    const nodes = (jsonLd || []).map((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });
    restore.push(() => nodes.forEach((n) => n.remove()));

    return () => restore.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, jsonLdKey]);
}
